import "./DeleteSectionModal.css";

import { overlay } from "overlay-kit";
import { type FC } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { serverDisconnectSection } from "api/api-server-actions";
import { t } from "i18n/i18n";
import { Checkbox } from "mds/components/Checkbox";
import { ConfirmationDialog } from "mds/components/ConfirmationDialog";
import { FormField } from "mds/components/FormField";
import { TextField } from "mds/components/TextField";
import { useAppSelector } from "store/index";
import { selectCurrentCourseCode, selectLmsType } from "store/selectors";
import { toastSuccessMessage } from "utils/alerts";

export const showDisconnectConfirmationModal = ({ sectionId }: { sectionId: string }) =>
  overlay.openAsync<boolean>(({ isOpen, close }) => (
    <DisconnectSectionModal
      open={isOpen}
      sectionId={sectionId}
      onClose={close}
      onConfirm={() => {}}
    />
  ));

type DisconnectSectionModalProps = {
  sectionId: string;
  open: boolean;
  onClose: (confirmed: boolean) => void;
  onConfirm: () => void;
};

type DisconnectSectionFormValues = {
  deletePagesAndAssessments: boolean;
  courseCodeConfirmation: string;
};

// eslint-disable-next-line react-refresh/only-export-components
const DisconnectSectionModal: FC<DisconnectSectionModalProps> = ({
  sectionId,
  open,
  onClose,
  onConfirm,
}) => {
  const lmsType = useAppSelector(selectLmsType);
  const courseCode = useAppSelector(selectCurrentCourseCode);
  const buttonText = t("confirm_disconnect_modal.button_text");

  const form = useForm<DisconnectSectionFormValues>({
    defaultValues: {
      deletePagesAndAssessments: true,
      courseCodeConfirmation: "",
    },
  });

  const courseCodeConfirmation = form.watch("courseCodeConfirmation");

  const handleDisconnect = async () => {
    const { deletePagesAndAssessments } = form.getValues();
    await serverDisconnectSection(sectionId, deletePagesAndAssessments);
    toastSuccessMessage(t("success.toasts.disconnect_section_success"));
    onConfirm();
  };

  return (
    <ConfirmationDialog
      confirmButtonDisabled={courseCodeConfirmation !== courseCode}
      confirmButtonKind="caution"
      confirmButtonText={buttonText}
      open={open}
      title={t("confirm_disconnect_modal.disconnect_item", { lms: lmsType })}
      onClose={onClose}
      onConfirm={handleDisconnect}
    >
      <FormProvider {...form}>
        <div className="section-delete-modal flex flex-col items-start gap-8">
          <div className="section-delete-warning">
            {t("confirm_disconnect_modal.disconnect_section_warning", { lms: lmsType })}
          </div>

          <Checkbox {...form.register("deletePagesAndAssessments")}>
            {t("confirm_disconnect_modal.clear_content_checkbox_text", { lms: lmsType })}
          </Checkbox>

          <FormField
            className="w-full"
            label={t("section_list_panel.course_confirm_label")}
            name="courseCodeConfirmation"
            required
          >
            {(controlProps) => (
              <TextField
                {...form.register("courseCodeConfirmation")}
                {...controlProps}
                placeholder={t("section_list_panel.course_placeholder")}
              />
            )}
          </FormField>
        </div>
      </FormProvider>
    </ConfirmationDialog>
  );
};
